import type { Audience, Privacy } from "@rhema/client";
import { Card, cn, focusRing, SettingRow, SettingsGroup, Switch } from "@rhema/ui";
import { useEffect, useState } from "react";
import { usePhoneEnabled } from "../session/features";
import { useApi } from "../session/session";

type AudienceKey =
  | "last_seen_visibility"
  | "photo_visibility"
  | "about_visibility"
  | "groups_add"
  | "findable_by_phone";

const AUDIENCE_ROWS: { key: AudienceKey; title: string; detail: string }[] = [
  {
    key: "last_seen_visibility",
    title: "Last seen and online",
    detail: "Who sees when you were last on Rhema",
  },
  { key: "photo_visibility", title: "Profile photo", detail: "Who sees your profile photo" },
  { key: "about_visibility", title: "About", detail: "Who sees your about line" },
  { key: "groups_add", title: "Groups", detail: "Who can add you to groups" },
  { key: "findable_by_phone", title: "Find me by number", detail: "Who can find you by your phone number" },
];

const AUDIENCES: { id: Audience; label: string }[] = [
  { id: "everyone", label: "Everyone" },
  { id: "contacts", label: "My contacts" },
  { id: "nobody", label: "Nobody" },
];

/** Who sees what, and who can reach you. "My contacts" means the people in your own contacts. */
export function PrivacySettings() {
  const api = useApi();
  const phone = usePhoneEnabled();
  const rows = AUDIENCE_ROWS.filter((row) => phone || row.key !== "findable_by_phone");
  const [privacy, setPrivacy] = useState<Privacy | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .privacy()
      .then(setPrivacy)
      .catch((e: Error) => setError(e.message));
  }, [api]);

  const change = (patch: Partial<Privacy>) => {
    if (!privacy) return;
    const before = privacy;
    setPrivacy({ ...privacy, ...patch });
    setError(null);
    api
      .updatePrivacy(patch)
      .then((saved) => setPrivacy((now) => ({ ...(now ?? before), ...saved })))
      .catch((e: Error) => {
        setPrivacy(before);
        setError(e.message);
      });
  };

  return (
    <section id="privacy" aria-labelledby="privacy-title" className="flex flex-col gap-2">
      <h2 id="privacy-title" className="m-0 px-1 pt-2 text-[15px] font-bold">
        Privacy
      </h2>
      {error && (
        <p role="alert" className="m-0 px-1 text-sm text-scarlet">
          {error}
        </p>
      )}
      {!privacy && !error && <p className="m-0 px-1 text-sm text-ink-meta">Loading your privacy settings.</p>}
      {privacy && (
        <>
          <Card className="flex flex-col gap-4 px-4 py-3.5">
            {rows.map((row) => (
              <fieldset key={row.key} className="m-0 flex flex-col gap-1.5 border-0 p-0">
                <legend className="p-0 text-[15px] font-semibold">{row.title}</legend>
                <span className="text-[12.5px] text-ink-secondary">{row.detail}</span>
                <div className="mt-1 flex gap-1 rounded-field bg-surface-header p-1">
                  {AUDIENCES.map((a) => {
                    const on = privacy[row.key] === a.id;
                    return (
                      <button
                        key={a.id}
                        type="button"
                        aria-pressed={on}
                        aria-label={`${row.title}: ${a.label}`}
                        onClick={() => change({ [row.key]: a.id })}
                        className={cn(
                          "flex h-9 flex-1 cursor-pointer items-center justify-center rounded-[9px] text-sm font-semibold",
                          on ? "bg-surface text-ink" : "bg-transparent text-ink-secondary",
                          focusRing,
                        )}
                      >
                        {a.label}
                      </button>
                    );
                  })}
                </div>
              </fieldset>
            ))}
          </Card>
          <SettingsGroup>
            <SettingRow
              title="Read receipts"
              detail="When this is off, you also do not see other people's read receipts"
              trailing={
                <Switch
                  label="Read receipts"
                  checked={privacy.read_receipts}
                  onChange={(on) => change({ read_receipts: on })}
                />
              }
            />
            <SettingRow
              title="Silence unknown callers"
              detail="Calls from people outside your contacts do not ring. They still show in your calls."
              trailing={
                <Switch
                  label="Silence unknown callers"
                  checked={privacy.silence_unknown_callers}
                  onChange={(on) => change({ silence_unknown_callers: on })}
                />
              }
            />
          </SettingsGroup>
        </>
      )}
    </section>
  );
}
